<template>
  <div class="login login-container fill-height mt-1 white">
    <v-container fluid fill-height>
      <v-layout>
        <v-flex xs12>
          <v-form
            ref="form"
            v-model="valid"
            @submit.prevent.stop="handleSubmit"
          >
            <v-card class="login-card elevation-0 px-0 px-sm-7 pb-5">
              <div
                class="login-title text-h3 line-height-1 default--text text-center mt-6 mb-2"
              >
                {{ $t("login.title") }}
              </div>
              <p class="login-subtitle text-center mb-0">
                {{ $t("login.subtitle") }}
              </p>
              <v-spacer></v-spacer>
              <v-card-text>
                <v-text-field
                  v-if="!showMfa"
                  color="primary"
                  v-model="email"
                  :error-messages="errors"
                  type="email"
                  :label="$t('login.email')"
                  autocomplete="off"
                  class="mb-2"
                  required
                  dense
                  outlined
                  clearable
                ></v-text-field>
                <v-text-field
                  v-if="!showMfa"
                  color="primary"
                  v-model="password"
                  :label="$t('login.password')"
                  autocomplete="off"
                  :append-icon="showPassword ? '$eye' : '$eyeOff'"
                  :type="showPassword ? 'text' : 'password'"
                  :rules="passwordRules"
                  @click:append="toggleShowPassword"
                  required
                  dense
                  outlined
                  clearable
                ></v-text-field>

                <template v-if="showMfa">
                  {{ $t("login.textMfa", { email: email }) }}
                  <v-otp-input
                    v-model="mfaPin"
                    :error-messages="errors"
                    :label="$t('login.mfaCode')"
                    :rules="mfaCodeRules"
                    dense
                    outlined
                    clearable
                /></template>

                <div class="d-flex flex-column remember-password">
                  <v-checkbox
                    color="primary"
                    class="remember-me-checkbox mt-0 mb-4 align-self-center"
                    hide-details
                    v-model="rememberMe"
                  >
                    <template v-slot:label>
                      <div class="remember-device text-caption">
                        {{ $t("login.rememberOnDevice") }}
                      </div>
                    </template>
                  </v-checkbox>
                  <div class="default--text mb-3 font-weight-bold text-right">
                    <a
                      @click="goToResetPassword"
                      class="reset-psw-link text-decoration-underline text-body-2"
                      :class="$vuetify.breakpoint.xs ? 'default--text' : ''"
                    >
                      {{ $t("login.resetPassword") }}
                    </a>
                  </div>

                  <div class="mt-2" style="margin-bottom: 20px;" v-if="showMfa">
                    <v-btn
                      v-if="showResendOtp"
                      large
                      text
                      width="100%"
                      color="primary"
                      @click="resendMfaPinAndLogin"
                    >
                      Invia di nuovo il codice
                    </v-btn>
                    <span v-else>
                      Puoi richiedere un nuovo codice tra
                      {{ resendOtpCountdown }}
                      secondi
                    </span>
                  </div>

                  <v-btn
                    :loading="loading"
                    type="submit"
                    class="login-btn"
                    color="primary"
                    large
                    block
                    depressed
                  >
                    {{ $t("login.buttonLogin") }}
                  </v-btn>
                  <RecaptchaDisclaimer />
                  <ResponseMessage :response="response" class="mt-3" />
                </div>

                <div
                  class="register-title text-h3 line-height-1 text-center mt-6 mb-2"
                >
                  {{ $t("login.registerTitle") }}
                </div>
                <span
                  class="register-subtitle text-body-1 text-center d-block font-weight-bold"
                >
                  {{ $t("login.registerSubtitle") }}
                </span>
                <p class="register-descr mb-0">
                  {{ $t("login.registerDescr") }}
                </p>
              </v-card-text>
              <v-card-actions class="d-flex flex-column">
                <v-btn
                  @click="$emit('submit', false)"
                  color="primary"
                  large
                  block
                  depressed
                  :to="{ name: 'RegistrationUser', path: '/registration-user' }"
                >
                  {{ $t("login.buttonRegister") }}
                </v-btn>
                <!-- <v-btn
                  @click.stop.prevent="facebookLogin()"
                  color="#3D6AD6"
                  depressed
                  large
                  block
                  class="facebook-btn white--text mt-3 ml-0"
                >
                  <v-icon class="mr-1">$facebook</v-icon>
                  <span>{{ $t("login.buttonFacebook") }}</span>
                </v-btn> -->
              </v-card-actions>
            </v-card>
          </v-form>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<style lang="scss">
.login-container {
  .default--text {
    a {
      color: var(--v-default-base) !important;
      font-weight: bold !important;
    }
  }
}
</style>

<script>
import ResponseMessage from "@/components/ResponseMessage";
// import CheckDisclaimerDialog from "@/components/profile/CheckDisclaimerDialog.vue";
import RecaptchaDisclaimer from "~/components/RecaptchaDisclaimer.vue";

import { mapActions, mapGetters } from "vuex";

import pushNotification from "~/mixins/pushNotification";

import SocialService from "~/service/socialService";
// import SecurityService from "~/service/securityService";

import {
  requiredValue,
  isPasswordMinimumLength
} from "~/validator/validationRules";

export default {
  name: "Login",
  props: {
    hideLogo: { type: Boolean, required: false, default: false },
    hideRegistration: { type: Boolean, required: false, default: false },
    redirectParams: { type: Object, required: false }
  },
  mixins: [pushNotification],
  components: { ResponseMessage, RecaptchaDisclaimer },
  data() {
    return {
      loading: null,
      loadingCardCode: null,
      showPassword: false,
      email: "",
      password: "",
      mfaPin: "",
      showMfa: false,
      resendOtpTimer: null,
      resendOtpCountdown: 180, // seconds
      resendMfaPin: false,
      rememberMe: true,
      response: {},
      valid: true,
      lazy: true,
      passwordRules: [
        requiredValue("Digitare la password"),
        isPasswordMinimumLength()
      ],
      errors: [],
      invalid: null,
      showResendOtp: false
    };
  },
  watch: {
    showMfa(newVal) {
      if (newVal) {
        this.startResendOtpTimer();
      } else {
        this.clearResendOtpTimer();
      }
    }
  },
  methods: {
    ...mapGetters({
      isAuthenticated: "cart/isAuthenticated"
    }),
    ...mapActions({
      doLogin: "cart/doLogin",
      loadCart: "cart/loadCart"
    }),
    goToResetPassword() {
      this.$router.push({
        name: "ResetPasswordRequest"
      });
      this.$emit("submit", false);
    },
    toggleShowPassword(e) {
      e.preventDefault();
      this.showPassword = !this.showPassword;
    },
    doFBLogin() {
      var _this = this;
      window.facebookConnectPlugin.login(
        ["public_profile", "email"],
        function(userData) {
          SocialService.facebookLoginConnect(
            userData.authResponse.accessToken,
            true
          ).then(
            function(data) {
              if (data.user.login && data.user.login != "") {
                if (data.data.socialRedirectUrl) {
                  data.data.socialRedirectUrl = "/dashboard";
                }
                _this.$emit("submit", false);
                global.vm.$router
                  .push({
                    path: data.data.socialRedirectUrl
                  })
                  .catch(err => err);
              } else {
                //NUOVO UTENTE
                if (data.data.socialRedirectUrl) {
                  data.data.socialRedirectUrl =
                    "/registration-nocard?social_login=facebook";
                }
                _this.$emit("submit", false);
                global.vm.$router
                  .push({
                    path: data.data.socialRedirectUrl
                  })
                  .catch(err => err);
                // SecurityService.refreshLogin(
                //   data.socialRedirectUrl.indexOf("registration") >= 0
                // ).then(
                //   function() {
                //     this.closeLogin();
                //   },
                //   function() {
                //     this.closeLogin();
                //   }
                // );
              }
            },
            function(error) {
              console.log(error);
            }
          );
        },
        function(error) {
          console.log(error);
        }
      );
    },
    facebookLogin() {
      if (this.isCordova) {
        this.doFBLogin();
      } else {
        let redirectBaseUrl = `${window.location.protocol}//${
          window.location.hostname
        }${
          window.location.port !== "80" &&
          window.location.port !== "443" &&
          window.location.port &&
          window.location.port !== ""
            ? ":" + window.location.port
            : ""
        }`;
        SocialService.openFacebookLoginPage(redirectBaseUrl);
      }
    },
    socialLoginGoogle() {
      if (this.isCordova) {
        let redirectBaseUrl =
          window.location.protocol +
          "//" +
          window.location.hostname +
          (window.location.port !== "80" &&
          window.location.port !== "443" &&
          window.location.port &&
          window.location.port !== ""
            ? ":" + window.location.port
            : "");

        let url =
          "https://accounts.google.com/o/oauth2/auth?client_id=" +
          global.config.googleClientId +
          "&redirect_uri=" +
          redirectBaseUrl +
          "/ebsn/api/social-redir/google&prompt=consent&response_type=code&scope=https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile";

        SocialService.handleGoogleOnApp(url, "registration-nocard");
      } else {
        SocialService.openGoogleLoginPage();
      }
      this.$emit("submit", false);
    },
    scanCard() {
      if (window.cordova && window.cordova.plugins.barcodeScanner) {
        try {
          this.loadingCardCode = true;
          // eslint-disable-next-line no-undef
          cordova.plugins.barcodeScanner.scan(
            result => {
              if (result) {
                this.loadingCardCode = false;
                // todo get card code and go to registration
              }
            },
            error => {
              this.loadingCardCode = false;
              console.error("Scanning failed", error);
            },
            {
              showFlipCameraButton: true,
              showTorchButton: true,
              resultDisplayDuration: 500, // Android, display scanned text for X ms. 0 suppresses it entirely, default 1500
              formats:
                this.formats ||
                "EAN_8,EAN_13,CODE_128,CODE_39,CODE_93,CODABAR,UPC_A,UPC_E"
            }
          );
        } catch (err) {
          console.log(err);
        }
      }
    },
    startResendOtpTimer() {
      this.showResendOtp = false;
      this.resendOtpCountdown = 180;
      if (this.resendOtpTimer) clearInterval(this.resendOtpTimer);
      this.resendOtpTimer = setInterval(() => {
        this.resendOtpCountdown--;
        if (this.resendOtpCountdown <= 0) {
          this.showResendOtp = true;
          clearInterval(this.resendOtpTimer);
          this.resendOtpTimer = null;
        }
      }, 1000);
    },
    clearResendOtpTimer() {
      if (this.resendOtpTimer) {
        clearInterval(this.resendOtpTimer);
        this.resendOtpTimer = null;
      }
      this.showResendOtp = false;
    },
    resendMfaPinAndLogin(e) {
      e.preventDefault();
      this.resendMfaPin = true;
      this.handleSubmit(e);
      this.resendMfaPin = false;
      this.showResendOtp = false;
      this.startResendOtpTimer();
    },
    async handleSubmit() {
      let _this = this;
      if (
        _this.showMfa &&
        !_this.resendMfaPin &&
        (!_this.mfaPin || _this.mfaPin.length === 0 || _this.mfaPin.length < 6)
      ) {
        return;
      }
      if (_this.password.length > 0) {
        try {
          _this.loading = true;
          _this.response = {};
          await _this.doLogin({
            email: _this.email,
            password: _this.password,
            rememberMe: _this.rememberMe,
            mfaPin: this.mfaPin,
            resendMfaPin: _this.resendMfaPin
          });
          if (_this.isAuthenticated) {
            global.EventBus.$emit("login");
            _this.$emit("loggedIn", true);
          }
          this.$emit("submit", true);
          if (_this.redirectParams) {
            _this.$router.push({
              name: _this.redirectParams.name,
              path: _this.redirectParams.path,
              params: _this.redirectParams.params
            });
          }
        } catch (response) {
          this.response = response;

          if (response.status == 4) {
            //need mfa
            this.showMfa = true;
          }
        } finally {
          this.loading = false;
        }
      }
    }
  },
  beforeDestroy() {
    this.clearResendOtpTimer();
  }
};
</script>
